<template>
  <div class="category-selector">
    <!-- Category Toggle -->
    <div class="row">
      <div class="col">
        <el-switch
          v-model="enableCategory"
          active-color="#e14eca"
          @change="onToggleCategory"
        />
        <label> &nbsp; Add resource to category</label>
      </div>
    </div>

    <!-- Category Selection -->
    <div class="row" v-if="enableCategory">
      <div class="col">
        <base-input label="Resource category" :error="error">
          <div class="category-select-row">
            <el-select
              :value="selectedCategoryId"
              @input="onCategorySelect"
              class="select-primary category-select"
              placeholder="Select category"
            >
              <el-option
                v-for="category in activeCategories"
                :label="category.name"
                :value="category.id"
                class="select-primary"
                :key="'CAT' + category.id"
              >
                <span :style="'color: ' + (category.color || '#666')">●</span>
                {{ category.name }}
              </el-option>
              <el-option
                v-if="canManageCategories"
                class="select-primary border-top"
                label="+ Create new category"
                value="CREATE_NEW"
                key="CREATE_NEW"
              ></el-option>
            </el-select>
            <base-button link icon
              v-if="activeCategories.length > 0 && canManageCategories"
              size="sm"
              type="primary"
              class="category-manage-btn"
              @click="showManagement = !showManagement"
            >
              <i class="tim-icons icon-settings-gear-63"></i>
            </base-button>
          </div>
        </base-input>
      </div>
    </div>

    <!-- Quick Create New Category Form -->
    <div class="row mb-3" v-if="enableCategory && selectedCategoryId === 'CREATE_NEW'">
      <div class="col">
        <div style="border: 1px solid #e14eca; border-radius: 8px; padding: 16px;">
          <h6>Create New Category</h6>
          <div class="row">
            <div class="col-lg-8">
              <base-input label="Category name" :error="newCategoryError">
                <el-input
                  v-model="newCategoryForm.name"
                  placeholder="Enter category name"
                  @keyup.enter.native="saveNewCategory"
                ></el-input>
              </base-input>
            </div>
            <div class="col-lg-4">
              <base-input label="Label color">
                <el-color-picker
                  v-model="newCategoryForm.color"
                  :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                ></el-color-picker>
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-button size="sm" type="secondary" @click="cancelNewCategory">Cancel</base-button>
              <base-button class="ml-2"
                size="sm"
                type="primary"
                @click="saveNewCategory"
                :disabled="!newCategoryForm.name || creatingCategory"
                :loading="creatingCategory"
              >
                Create category
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Management Panel -->
    <div class="row mb-3" v-if="enableCategory && showManagement">
      <div class="col">
        <div style="border: 1px solid #e14eca; border-radius: 8px; padding: 5px 16px 5px 16px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <h6 style="margin: 0;">Manage Categories</h6>
            <base-button icon size="sm" link class="text-white" @click="showManagement = false">
              <i class="tim-icons icon-simple-remove"></i>
            </base-button>
          </div>

          <!-- Active Categories -->
          <div v-if="activeCategories.length > 0">
            <b-table :items="activeCategories"
                     :fields="[{ key: 'name', tdClass: 'align-top' },
                         { key: 'actions', tdClass: 'align-center' }]">
              <template v-slot:cell(name)="row">
                <div class="category-name-cell">
                  <span v-if="!row.item.editing" :style="'color: ' + (row.item.color || '#666')">●</span>
                  <el-color-picker v-if="row.item.editing"
                    v-model="row.item.editColor"
                    :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                  ></el-color-picker>
                  <span class="text-white" v-if="!row.item.editing">{{ row.item.name }}</span>
                  <base-input
                    v-if="row.item.editing"
                    v-model="row.item.editName"
                    size="sm"
                    class="category-name-input"
                  ></base-input>
                </div>
              </template>
              <template v-slot:cell(actions)="row">
                <el-dropdown v-if="!row.item.editing">
                  <base-button
                    simple
                    type="primary"
                    link
                  > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item" href="#" @click.prevent="startEditCategory(row.item)">
                        <i class="tim-icons icon-pencil mr-2"></i> Edit category
                      </a>
                      <hr>
                      <a class="dropdown-item" href="#" @click.prevent="archiveCategory(row.item)">
                        <i class="tim-icons icon-trash-simple mr-2"></i> Archive category
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
                <div v-if="row.item.editing" class="category-edit-actions">
                  <base-button
                    size="sm"
                    type="secondary"
                    icon
                    :disabled="savingEditId === row.item.id"
                    @click="cancelEditCategory(row.item)"
                    title="Cancel"
                  >
                    <i class="tim-icons icon-simple-remove"></i>
                  </base-button>
                  <base-button
                    size="sm"
                    type="primary"
                    icon
                    :disabled="savingEditId === row.item.id || !row.item.editName"
                    @click="saveCategoryEdit(row.item)"
                    title="Save"
                  >
                    <i
                      v-if="savingEditId === row.item.id"
                      class="el-icon-loading category-save-spinner"
                    ></i>
                    <i v-else class="tim-icons icon-check-2"></i>
                  </base-button>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {ColorPicker, Dropdown, DropdownItem, DropdownMenu, Switch} from "element-ui";
import { API_LOCATION } from '@/api-config'
import {Auth} from "aws-amplify";
import axios from "axios";
import {BTable} from "bootstrap-vue";

export default {
  name: 'ResourceCategorySelector.vue',
  components: {
    [Switch.name]: Switch,
    [ColorPicker.name]: ColorPicker,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    BTable
  },
  props: {
    companyGroupId: {
      type: Number,
      required: true
    },
    initialCategoryId: {
      type: [Number, String],
      default: null
    },
    error: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      enableCategory: false,
      selectedCategoryId: null,
      showManagement: false,
      showArchived: false,
      activeCategories: [],

      // New category form
      newCategoryForm: {
        name: '',
        color: '#e14eca',
      },
      newCategoryError: null,
      creatingCategory: false,

      // Quick create in management
      quickCreateName: '',
      quickCreateColor: '#e14eca',

      // Inline-edit save guard (id of row currently saving, null otherwise)
      savingEditId: null,
      // Archive guard (id of row currently being archived, null otherwise)
      archivingId: null,
    }
  },

  watch: {
    initialCategoryId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.enableCategory = true;
          this.selectedCategoryId = newVal;
        } else {
          this.enableCategory = false;
          this.selectedCategoryId = null;
        }
      }
    },

    companyGroupId: {
      immediate: true,
      handler() {
        this.loadCategories();
      }
    }
  },

  computed: {
    canManageCategories() {
      try {
        const permissions = JSON.parse(localStorage.getItem('permissions')) || [];
        return permissions.includes('resource_slots_update');
      } catch (e) {
        return false;
      }
    }
  },

  methods: {
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken);
          })
          .catch(err => console.log(err));
      });
    },
    async loadCategories() {
      if (!this.companyGroupId) return;

      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let companyId = localStorage.getItem('companyId');
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.companyGroupId + '/resource-categories';

        const response = await axios.get(url, config);
        this.activeCategories = response.data.resource_categories || [];
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    },

    onToggleCategory(enabled) {
      if (!enabled) {
        this.selectedCategoryId = null;
        this.showManagement = false;
      }
      this.$emit('category-changed', {
        enabled: enabled,
        categoryId: this.selectedCategoryId,
        category: this.getSelectedCategory()
      });
    },

    onCategorySelect(value) {
      this.selectedCategoryId = value;
      if (value !== 'CREATE_NEW') {
        this.$emit('category-changed', {
          enabled: this.enableCategory,
          categoryId: value,
          category: this.getSelectedCategory()
        });
      }
    },

    getSelectedCategory() {
      return this.activeCategories.find(cat => cat.id === this.selectedCategoryId) || null;
    },

    async saveNewCategory() {
      if (!this.newCategoryForm.name) return;

      this.creatingCategory = true;
      this.newCategoryError = null;

      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let companyId = localStorage.getItem('companyId');
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.companyGroupId + '/resource-categories';

        const response = await axios.post(url, {
          name: this.newCategoryForm.name,
          color: this.newCategoryForm.color,
          description: this.newCategoryForm.description
        }, config);

        const newCategory = response.data;
        this.activeCategories.push(newCategory);
        this.selectedCategoryId = newCategory.id;
        this.resetNewCategoryForm();

        this.$emit('category-changed', {
          enabled: this.enableCategory,
          categoryId: newCategory.id,
          category: newCategory
        });

      } catch (error) {
        this.newCategoryError = error.response?.data?.message || 'Failed to create category';
      } finally {
        this.creatingCategory = false;
      }
    },

    cancelNewCategory() {
      this.selectedCategoryId = null;
      this.resetNewCategoryForm();
    },

    resetNewCategoryForm() {
      this.newCategoryForm = {
        name: '',
        color: '#e14eca',
        description: ''
      };
      this.newCategoryError = null;
    },

    // Management methods
    startEditCategory(category) {
      this.$set(category, 'editing', true);
      this.$set(category, 'editName', category.name);
      this.$set(category, 'editColor', category.color);
    },

    cancelEditCategory(category) {
      this.$set(category, 'editing', false);
      this.$delete(category, 'editName');
    },

    async saveCategoryEdit(category) {
      // Double-submit guard: if a save is already in flight for this row, ignore.
      if (this.savingEditId === category.id) return;
      if (!category.editName) return;
      this.savingEditId = category.id;
      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let companyId = localStorage.getItem('companyId');
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.companyGroupId + '/resource-categories/' + category.id;

        await axios.put(url, {
          name: category.editName,
          color: category.editColor
        }, config);

        category.name = category.editName;
        category.color = category.editColor;
        this.cancelEditCategory(category);
        this.$notify({ type: 'success', message: 'Category updated', timeout: 2000 });

      } catch (error) {
        console.error('Failed to update category:', error);
        this.$notify({ type: 'danger', message: 'Failed to update category', timeout: 3000 });
      } finally {
        this.savingEditId = null;
      }
    },

    async archiveCategory(category) {
      if (this.archivingId === category.id) return;
      this.archivingId = category.id;
      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let companyId = localStorage.getItem('companyId');
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.companyGroupId + '/resource-categories/' + category.id;

        await axios.delete(url,  config);

        // Move from active to archived
        const index = this.activeCategories.findIndex(c => c.id === category.id);
        if (index > -1) {
          this.activeCategories.splice(index, 1);
        }

        // Clear selection if this category was selected
        if (this.selectedCategoryId === category.id) {
          this.selectedCategoryId = null;
          this.onCategorySelect(null);
        }

        this.$notify({ type: 'success', message: 'Category archived', timeout: 2000 });

      } catch (error) {
        console.error('Failed to archive category:', error);
        this.$notify({ type: 'danger', message: 'Failed to archive category', timeout: 3000 });
      } finally {
        this.archivingId = null;
      }
    },

    async quickCreateCategory() {
      if (!this.quickCreateName) return;

      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let companyId = localStorage.getItem('companyId');
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.companyGroupId + '/resource-categories';

        const response = await axios.post(url, {
          name: this.quickCreateName,
          color: this.quickCreateColor
        }, config);

        const newCategory = response.data;
        this.activeCategories.push(newCategory);
        this.quickCreateName = '';
        this.quickCreateColor = '#e14eca';

      } catch (error) {
        this.$message.error('Failed to create category');
      }
    },

    refreshCategories() {
      this.loadCategories();
    }
  }
}
</script>

<style scoped>
.toggle-row {
  display: flex;
  align-items: center;
}

.toggle-row label {
  color: #fff;
  font-size: 13px;
}

.category-management-panel {
  border: 1px solid #f4f5f7;
  border-radius: 8px;
  padding: 16px;
  background: #fafbfc;
}

.border-top {
  border-top: 1px solid #eee !important;
  margin-top: 8px;
  padding-top: 8px;
}

.category-select-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.category-select {
  flex: 1;
  min-width: 0;
}

.category-select ::v-deep .el-select {
  width: 100%;
}

.category-manage-btn {
  flex-shrink: 0;
}

.category-edit-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  justify-content: flex-end;
}

.category-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.category-name-input {
  flex: 1;
  min-width: 0;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

::v-deep .category-name-input .form-group {
  margin-bottom: 0;
}

.category-save-spinner {
  font-size: 14px;
  line-height: 1;
}
</style>

<style>
.white-content .category-selector .toggle-row label {
  color: #525f7f;
}
</style>
